import { Avatar, Box, IconButton, Link, Typography } from '@mui/material';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { applicantService } from '../../../../services/applicant.service';
import type { ApplicantMessage, CommunicationMedium } from '../../../../services/types/applicant.types';
import {
  formatDateSeparator,
  formatTime,
  getFilenameFromUrl,
  getInitials,
  getQuotePreviewText,
  groupMessagesByDate,
  isLastInMessageBurst,
  isSameMessageBurst,
  normalizeApplicantMessagesResponse,
  parseWhatsAppFormattedText,
  resolveQuotedMessage,
  shouldShowSendWelcome,
  sortMessagesByDateTime,
  type WhatsAppTextSegment,
} from '../../../../utils/messageUtils';
import Alert from '../../../common/Alert';
import Button from '../../../common/Button';
import Loading from '../../../common/Loading';
import Notification from '../../../common/Notification';
import { useAuth } from '../../../../hooks/useAuth';
import { useNotification } from '../../../../hooks/useNotification';
import { usePermissions } from '../../../../context/PermissionContext';
import { PAGE_IDS, PERMISSION_IDS } from '../../../../constants/permissions';
import EyepaxLogo from '../../../../assets/icons/Eyepax Symbol 1.5x.svg';
import TextMessageIcon from '../../../../assets/icons/Text message icon.svg';

interface MessagesProps {
  applicantName?: string;
  applicantMobile?: string;
  communicationMedium?: CommunicationMedium | null;
}

interface ManualWelcomeResponse {
  success: boolean;
  message: string;
  welcomeSent: boolean;
  missingFieldTriggered: boolean;
}

const MEDIUM_ICON_MAP: Record<CommunicationMedium, React.ReactNode> = {
  WHATSAPP: <WhatsAppIcon sx={{ fontSize: 14, color: '#25D366' }} />,
  EMAIL: <EmailOutlinedIcon sx={{ fontSize: 14, color: '#702DFF' }} />,
  ZALO: <ChatBubbleOutlineIcon sx={{ fontSize: 14, color: '#0068FF' }} />,
};

const messageBodyTypographySx = {
  fontSize: '13.5px',
  lineHeight: '1.5',
  wordWrap: 'break-word',
  overflowWrap: 'break-word',
  whiteSpace: 'pre-wrap',
  color: '#344054',
} as const;

const WhatsAppFormattedText: React.FC<{ text: string }> = ({ text }) => {
  const segments = parseWhatsAppFormattedText(text);

  const renderSegments = (parts: WhatsAppTextSegment[], keyPrefix: string): React.ReactNode[] =>
    parts.map((segment, index) => {
      const key = `${keyPrefix}-${index}`;
      switch (segment.kind) {
        case 'text':
          return <React.Fragment key={key}>{segment.text}</React.Fragment>;
        case 'bold':
          return (
            <Box component="span" key={key} sx={{ fontWeight: 600 }}>
              {renderSegments(segment.children, key)}
            </Box>
          );
        case 'italic':
          return (
            <Box component="span" key={key} sx={{ fontStyle: 'italic' }}>
              {renderSegments(segment.children, key)}
            </Box>
          );
        case 'strike':
          return (
            <Box component="span" key={key} sx={{ textDecoration: 'line-through' }}>
              {renderSegments(segment.children, key)}
            </Box>
          );
        case 'mono':
          return (
            <Box
              component="span"
              key={key}
              sx={{ fontFamily: 'monospace', fontSize: '12px' }}
            >
              {renderSegments(segment.children, key)}
            </Box>
          );
        default:
          return null;
      }
    });

  return (
    <Typography sx={messageBodyTypographySx} component="div">
      {renderSegments(segments, 'seg')}
    </Typography>
  );
};

const QuotePreview: React.FC<{
  quoted: ApplicantMessage;
  applicantName: string;
  isCompanyBubble: boolean;
}> = ({ quoted, applicantName, isCompanyBubble }) => {
  const quotedFromCompany = quoted.sender_type !== 0;
  const quoteAuthor = quotedFromCompany ? 'Eyepax' : applicantName;
  const accentColor = quotedFromCompany ? '#702DFF' : '#25D366';

  return (
    <Box
      data-testid={`quote-preview-${quoted.id}`}
      sx={{
        borderLeft: `4px solid ${accentColor}`,
        backgroundColor: isCompanyBubble ? '#E8EBFF' : '#EAECF0',
        borderRadius: '4px',
        px: 1,
        py: 0.75,
        mb: 1,
      }}
    >
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 600,
          color: accentColor,
          lineHeight: 1.3,
        }}
      >
        {quoteAuthor}
      </Typography>
      <Typography
        sx={{
          fontSize: '12px',
          color: '#667085',
          lineHeight: 1.4,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
        }}
      >
        {getQuotePreviewText(quoted)}
      </Typography>
    </Box>
  );
};

const DeletedMessagePlaceholder: React.FC<{
  messageId: number;
  revealed: boolean;
  onToggle: () => void;
}> = ({ messageId, revealed, onToggle }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.5,
    }}
  >
    <BlockOutlinedIcon sx={{ fontSize: 16, color: '#667085' }} aria-hidden />
    <Typography
      sx={{
        fontSize: '13.5px',
        fontStyle: 'italic',
        color: '#667085',
        flex: 1,
      }}
    >
      This message was deleted
    </Typography>
    <IconButton
      size="small"
      onClick={onToggle}
      aria-label={revealed ? 'Hide deleted message content' : 'Show deleted message content'}
      data-testid={`deleted-message-toggle-${messageId}`}
      sx={{ color: '#667085', padding: '4px' }}
    >
      <VisibilityOutlinedIcon sx={{ fontSize: 18 }} />
    </IconButton>
  </Box>
);

const AttachmentRenderer: React.FC<{ message: ApplicantMessage }> = ({ message }) => {
  const { message_type, attachment_url, message_text } = message;

  if (message_type === 'TEXT' || !attachment_url) {
    return null;
  }

  switch (message_type) {
    case 'IMAGE':
      return (
        <Box sx={{ mt: message_text ? 1 : 0 }}>
          <a href={attachment_url} target="_blank" rel="noopener noreferrer">
            <Box
              component="img"
              src={attachment_url}
              alt="Attachment"
              sx={{
                maxWidth: 300,
                borderRadius: '8px',
                display: 'block',
              }}
            />
          </a>
        </Box>
      );
    case 'DOCUMENT':
      return (
        <Box sx={{ mt: message_text ? 1 : 0, display: 'flex', alignItems: 'center', gap: 1 }}>
          <DescriptionOutlinedIcon sx={{ fontSize: 19, color: '#667085' }} />
          <Link
            href={attachment_url}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ fontSize: '13.5px', color: '#702DFF', textDecoration: 'underline' }}
          >
            {getFilenameFromUrl(attachment_url)}
          </Link>
        </Box>
      );
    case 'AUDIO':
      return (
        <Box sx={{ mt: message_text ? 1 : 0 }}>
          <audio controls style={{ maxWidth: 300 }}>
            <source src={attachment_url} />
          </audio>
        </Box>
      );
    case 'VIDEO':
      return (
        <Box sx={{ mt: message_text ? 1 : 0 }}>
          <video controls style={{ maxWidth: 300, borderRadius: 8 }}>
            <source src={attachment_url} />
          </video>
        </Box>
      );
    default:
      return null;
  }
};

const MediumBadge: React.FC<{ medium: CommunicationMedium }> = ({ medium }) => (
  <Box
    data-testid={`medium-badge-${medium}`}
    sx={{
      position: 'absolute',
      bottom: -2,
      right: -2,
      width: 20,
      height: 20,
      borderRadius: '50%',
      backgroundColor: '#FFFFFF',
      border: '1px solid #EAECF0',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    {MEDIUM_ICON_MAP[medium]}
  </Box>
);

const MessageBubble: React.FC<{
  message: ApplicantMessage;
  allMessages: ApplicantMessage[];
  applicantName: string;
  senderChanged: boolean;
  showTimestamp: boolean;
  sameBurst: boolean;
  deletedContentRevealed: boolean;
  onToggleDeletedContent: () => void;
}> = ({
  message,
  allMessages,
  applicantName,
  senderChanged,
  showTimestamp,
  sameBurst,
  deletedContentRevealed,
  onToggleDeletedContent,
}) => {
  const isCompany = message.sender_type !== 0;
  const quotedMessage = resolveQuotedMessage(allMessages, message);
  const isDeleted = message.is_deleted === true;
  const showBody = Boolean(message.message_text) && (!isDeleted || deletedContentRevealed);

  return (
    <Box
      sx={{
        display: 'flex',
        justifyContent: isCompany ? 'flex-end' : 'flex-start',
        alignItems: 'flex-start',
        gap: 1,
        mb: sameBurst ? 0.5 : 1,
      }}
    >
      {!isCompany && senderChanged && (
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Avatar
            sx={{
              width: 38,
              height: 38,
              backgroundColor: '#702DFF',
              color: '#FFFFFF',
              fontSize: '15px',
              fontWeight: 600,
            }}
            aria-label={applicantName}
          >
            {getInitials(applicantName)}
          </Avatar>
          <MediumBadge medium={message.communication_medium} />
        </Box>
      )}
      {!isCompany && !senderChanged && <Box sx={{ width: 38, flexShrink: 0 }} />}

      <Box
        sx={{
          maxWidth: '55%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: isCompany ? 'flex-end' : 'flex-start',
        }}
      >
        <Box
          sx={{
            backgroundColor: isCompany ? '#F1F3FF' : '#F9FAFB',
            borderRadius: '8px',
            padding: '12px 16px',
          }}
        >
          {quotedMessage && (
            <QuotePreview
              quoted={quotedMessage}
              applicantName={applicantName}
              isCompanyBubble={isCompany}
            />
          )}
          {isDeleted && !deletedContentRevealed && (
            <DeletedMessagePlaceholder
              messageId={message.id}
              revealed={deletedContentRevealed}
              onToggle={onToggleDeletedContent}
            />
          )}
          {isDeleted && deletedContentRevealed && (
            <>
              <DeletedMessagePlaceholder
                messageId={message.id}
                revealed={deletedContentRevealed}
                onToggle={onToggleDeletedContent}
              />
              {message.message_text && (
                <Box sx={{ mt: 1, opacity: 0.85 }}>
                  <WhatsAppFormattedText text={message.message_text} />
                </Box>
              )}
            </>
          )}
          {!isDeleted && showBody && message.message_text && (
            <WhatsAppFormattedText text={message.message_text} />
          )}
          {(!isDeleted || deletedContentRevealed) && <AttachmentRenderer message={message} />}
        </Box>

        {showTimestamp && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: '2px' }}>
            {message.is_edited && (
              <Typography sx={{ fontSize: '11px', color: '#667085', fontStyle: 'italic' }}>
                Edited
              </Typography>
            )}
            <Typography sx={{ fontSize: '11px', color: '#667085' }}>
              {formatTime(message.sent_time)}
            </Typography>
          </Box>
        )}
      </Box>

      {isCompany && senderChanged && (
        <Box sx={{ position: 'relative', flexShrink: 0 }}>
          <Avatar
            sx={{
              width: 38,
              height: 38,
              backgroundColor: '#F1F3FF',
            }}
            aria-label="Company"
          >
            <Box
              component="img"
              src={EyepaxLogo}
              alt="Company logo"
              sx={{ width: 28, height: 28 }}
            />
          </Avatar>
          <MediumBadge medium={message.communication_medium} />
        </Box>
      )}
      {isCompany && !senderChanged && <Box sx={{ width: 38, flexShrink: 0 }} />}
    </Box>
  );
};

const Messages: React.FC<MessagesProps> = ({
  applicantName = 'Applicant',
  communicationMedium = 'WHATSAPP',
}) => {
  const { id } = useParams();
  const { user } = useAuth();
  const { pageId, setPageId, hasPermission } = usePermissions();
  const [isLoading, setIsLoading] = useState(true);
  const [messages, setMessages] = useState<ApplicantMessage[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSendingWelcome, setIsSendingWelcome] = useState(false);
  const { notification, showNotification, hideNotification } = useNotification();
  const [applicantId, setApplicantId] = useState<number | null>(null);
  const [applicantCommunicationMedium, setApplicantCommunicationMedium] =
    useState<CommunicationMedium | null>(null);
  const [canSendManualWelcome, setcanSendManualWelcome] = useState(false);
  const [revealedDeletedMessageIds, setRevealedDeletedMessageIds] = useState<Set<number>>(
    () => new Set(),
  );

  const toggleDeletedMessageReveal = (messageId: number) => {
    setRevealedDeletedMessageIds((prev) => {
      const next = new Set(prev);
      if (next.has(messageId)) {
        next.delete(messageId);
      } else {
        next.add(messageId);
      }
      return next;
    });
  };

  const fetchMessages = async () => {
    if (!id) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);

      const response = await applicantService.getMessages(id);

      const data = normalizeApplicantMessagesResponse(response.data);

      setApplicantId(data.applicantId ?? null);
      setApplicantCommunicationMedium(data.communicationMedium ?? null);
      setcanSendManualWelcome(data.canSendManualWelcome ?? false);
      setMessages(data.messages);

      setError(null);
    } catch (error) {
      console.error(error);
      setError('Failed to load messages');
    } finally {
      setIsLoading(false);
    }
  };



  useEffect(() => {
    if (pageId !== PAGE_IDS.APPLICANT_MESSAGES) {
      setPageId(PAGE_IDS.APPLICANT_MESSAGES);
    }
  }, [pageId, setPageId]);

  useEffect(() => {
    fetchMessages();
  }, [id]);

  if (isLoading) {
    return <Loading />;
  }

  if (error) {
    return (
      <Box sx={{ py: 2 }}>
        <Alert message={error} severity="error" />
      </Box>
    );
  }

  const handleSendWelcome = async () => {
    if (!id || isSendingWelcome) return;

    try {
      setIsSendingWelcome(true);

      if (!applicantId) {
        showNotification('Applicant ID not found', 'error');
        return;
      }


      const response = await applicantService.manualWelcome(
        applicantId,
        user?.email || ''
      );

      const data = response.data as ManualWelcomeResponse;

      showNotification(
        data.message || 'Welcome notification sent successfully',
        'success'
      );

      // Optional extra notification if vacancy code was requested
      if (data.missingFieldTriggered) {
        showNotification(
          'Welcome sent. Candidate was also asked for their vacancy code.',
          'success'
        );
      }
      setTimeout(() => {
        fetchMessages();
      }, 2000);

    } catch (error: any) {
      const message = error?.response?.data?.message;

      switch (message) {
        case 'welcome notification already sent via WhatsApp':
          showNotification(
            'Welcome notification was already sent via WhatsApp.',
            'error'
          );
          break;


        case 'only WHATSAPP applicants eligible (current: EMAIL)':
          showNotification(
            'Only WhatsApp applicants are eligible.',
            'error'
          );
          break;

        default:
          showNotification(
            message || 'Failed to send welcome message.',
            'error'
          );
      }
    } finally {
      setIsSendingWelcome(false);
    }
  };


  const showSendWelcome = shouldShowSendWelcome(
    messages,
    applicantCommunicationMedium as CommunicationMedium
  );
  const welcomeButtonVisible =
    canSendManualWelcome &&
    showSendWelcome &&
    hasPermission(PERMISSION_IDS.APPLICANT_MESSAGES_MANUAL_WELCOME);

  const sendWelcomeButton = (
    <Button
      variant="contained"
      width="234px"
      disableElevation
      isLoading={isSendingWelcome}
      onClick={handleSendWelcome}
      aria-label="Send Welcome"
      data-testid="send-welcome-button"
      startIcon={
        < img
          src={TextMessageIcon}
          alt="Text Message"
          width={21}
          height={21}
        />
      }
      sx={{
        borderRadius: '6px',
        height: '40px',
        fontFamily: 'Inter',
        fontSize: '16px',
        fontWeight: 500,
        paddingLeft: '10px',
        paddingRight: '10px',
      }}
    >
      Send Welcome
    </Button>
  );

  if (messages.length === 0) {
    return (
      <>
        <Box
          className="view-section"
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            padding: '10px 24px',
          }}
        >
          <Typography sx={{ color: '#667085', fontSize: '13.5px' }}>
            No messages yet
          </Typography>
          {welcomeButtonVisible && sendWelcomeButton}
        </Box>
        <Notification
          open={notification.open}
          onClose={hideNotification}
          message={notification.message}
          severity={notification.severity}
          title={notification.title}
        />
      </>
    );
  }

  const sorted = sortMessagesByDateTime(messages);
  const grouped = groupMessagesByDate(sorted);
  const sortedDates = Object.keys(grouped).sort();

  return (
    <>
      {welcomeButtonVisible && (
        <Box className="view-section" sx={{ display: 'flex', justifyContent: 'flex-end', padding: '10px 24px' }}>
          {sendWelcomeButton}
        </Box>
      )}
    <Box
      className="view-section"
      data-testid="messages-container"
      sx={{ maxHeight: 'calc(100vh - 320px)', overflowY: 'auto', scrollBehavior: 'smooth' }}
    >
      {sortedDates.map((dateKey) => (
        <React.Fragment key={dateKey}>
          <Box sx={{ display: 'flex', justifyContent: 'center', my: 1.5 }}>
            <Box
              sx={{
                backgroundColor: '#F9FAFB',
                borderRadius: '12px',
                px: 1.5,
                py: 0.5,
              }}
            >
              <Typography sx={{ fontSize: '12px', color: '#667085', fontWeight: 500 }}>
                {formatDateSeparator(dateKey)}
              </Typography>
            </Box>
          </Box>

          {grouped[dateKey].map((message, index) => {
            const arr = grouped[dateKey];
            const prev = index > 0 ? arr[index - 1] : null;
            const next = index < arr.length - 1 ? arr[index + 1] : null;
            const senderChanged = !prev || prev.sender_type !== message.sender_type;
            const sameBurst = isSameMessageBurst(prev, message);
            const showTimestamp = isLastInMessageBurst(message, next);

            return (
              <MessageBubble
                key={message.id}
                message={message}
                allMessages={sorted}
                applicantName={applicantName}
                senderChanged={senderChanged}
                showTimestamp={showTimestamp}
                sameBurst={sameBurst}
                deletedContentRevealed={revealedDeletedMessageIds.has(message.id)}
                onToggleDeletedContent={() => toggleDeletedMessageReveal(message.id)}
              />
            );
          })}
        </React.Fragment>
      ))}
    </Box>
    <Notification
      open={notification.open}
      onClose={hideNotification}
      message={notification.message}
      severity={notification.severity}
      title={notification.title}
    />
    </>
  );
};

export default Messages;
